import { useRef } from 'react'
import { Box, Button, Skeleton, Stack } from '@mui/material'
import useLocalStorage from '@/hooks/useLocalStorage'
import { useGetOrgCredentialType } from '@/service-library/hooks/org-credential-types'
import { ProjectGridField } from '@/types/fields'
import { LiveListEntity } from '@/types/live-lists'
import { BaseVendor } from '@/types/relay'
import { VENDOR_INFO_TYPE_IDS } from '@/utils/info-types'
import FlagsBulletList from '@/components/flags/FlagsBulletList'
import { useDocumentContext } from '@/components/image-zoom-pan/providers/DocumentProvider'
import CredentialTypeProvider from '@/components/integration-settings/CredentialTypeProvider'
import { useProjectContext } from '@/components/project-tables/ProjectProvider'
import LiveListPickerField, {
  liveListNoneOption,
} from '@/components/validation/fields/LiveListPickerField'
import FocusContainer from '@/components/validation/FocusContainer'
import { useProjectGridFieldsContext } from '@/components/validation/ProjectGridFieldsProvider'
import { useSelectedFieldContext } from '@/components/validation/providers/SelectedFieldProvider'
import useFieldValue from '@/components/validation/useFieldValue'
import { useDocumentRowsContext } from '../validation/providers/DocumentRowsProvider'
import NewVendorButton from './NewVendorButton'
import VendorDetails from './VendorDetails'
import VendorSettings from './VendorSettings'

export default function VendorCard() {
  const fields = useProjectGridFieldsContext()
  const { isDisabled, document } = useDocumentContext()
  const { project } = useProjectContext()
  const { setSelectedField, isSelectedField } = useSelectedFieldContext() || {}
  const { baseRow } = useDocumentRowsContext()
  const [showMore, setShowMore] = useLocalStorage(
    'vendor-card-show-more',
    false,
  )

  const focusContainerRef = useRef<HTMLDivElement>(null)

  // We don't render this component unless there is a vendor field,
  // so we don't need to check that here.
  const vendorField = fields.find((field) =>
    VENDOR_INFO_TYPE_IDS.includes(field.info_type_id as string),
  ) as ProjectGridField

  const {
    value,
    dataLineageValues,
    jsonValue,
    onManualChange,
    documentRowValue,
  } = useFieldValue({
    field: vendorField,
  })

  const rowValueFlags = documentRowValue?.row_value_flags || []

  const { orgCredentialType } = useGetOrgCredentialType({
    id: project.org_credential_type_id || '',
  })

  const allowsVendorSettings = orgCredentialType?.allowed_configuration.some(
    (config) => config.startsWith('vendor_'),
  )

  return vendorField && orgCredentialType ? (
    <CredentialTypeProvider credentialType={orgCredentialType}>
      <Stack spacing={2}>
        <Box>
          <FocusContainer
            focusContainerRef={focusContainerRef}
            isSelectedField={
              !!isSelectedField?.(
                vendorField.id,
                documentRowValue?.document_row_id || null,
              )
            }
            isUserEditable={vendorField.input_behavior !== 'manual_not_allowed'}
            onFocus={() => {
              !isDisabled &&
                setSelectedField?.({
                  document_row_id: documentRowValue?.document_row_id as string,
                  field: vendorField,
                })
            }}
            onBlur={() => {
              setSelectedField?.(null)
            }}
          >
            {baseRow && (
              <LiveListPickerField
                value={
                  value === '' && dataLineageValues.final_value === ''
                    ? liveListNoneOption
                    : (jsonValue as LiveListEntity)
                }
                documentRow={baseRow}
                field={vendorField}
                onChange={(inputField, refId, jsonObject) => {
                  onManualChange({
                    manualValue: inputField,
                    refId,
                    jsonObject,
                  })
                }}
                label="Vendor"
                slotProps={{
                  inputLabel: {
                    shrink: true,
                  },
                }}
              />
            )}
          </FocusContainer>
          {rowValueFlags.length > 0 && (
            <FlagsBulletList rowValueFlags={rowValueFlags} />
          )}
        </Box>
        {jsonValue && (
          <VendorDetails
            credentialType={orgCredentialType}
            entityJson={jsonValue as LiveListEntity}
          />
        )}

        {showMore && document?.owner_org_id && jsonValue?.entity_id && (
          <VendorSettings
            orgId={document.owner_org_id}
            vendor={jsonValue as LiveListEntity<BaseVendor>}
          />
        )}
        {((jsonValue?.entity_id && allowsVendorSettings) ||
          orgCredentialType.supports?.create_new_vendors) && (
          <Stack
            direction="row"
            justifyContent={jsonValue?.entity_id ? 'space-between' : 'flex-end'}
            alignItems="center"
            sx={{}}
          >
            {jsonValue?.entity_id && allowsVendorSettings && (
              <Button
                variant="text"
                onClick={() => setShowMore((prev) => !prev)}
                sx={{ ml: '-5px' }}
              >
                Show {showMore ? 'Less' : 'More'}
              </Button>
            )}
            {orgCredentialType.supports?.create_new_vendors && (
              <NewVendorButton
                onCreate={(newVendor: LiveListEntity) => {
                  onManualChange({
                    manualValue: newVendor.pd_display_name,
                    jsonObject: newVendor,
                  })
                }}
              />
            )}
          </Stack>
        )}
      </Stack>
    </CredentialTypeProvider>
  ) : (
    <>
      <Skeleton width="100%" />
      <Skeleton width="75%" />
    </>
  )
}
